import { Component, Input, OnDestroy, OnInit, output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BreadcrumbItem,
  QaiInnerWrapComponent,
  QaiStepFlowComponent,
  QaiStepCardComponent,
  QaiLoadingComponent,
  type StepState,
  type QuizAttempt,
  type RolePlayAttempt,
  type HeaderConfig,
  QaiAlertComponent,
  QaiButtonComponent,
  QaiStepTableComponent,
  QaiBadgeComponent,
  QaiToastComponent,
  QaiToastService,
} from '@qai/ui';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslationService } from 'src/app/services/translation.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';
import { PathwaysService } from 'src/app/services/learner/pathways.service';
import { IPathwayEnrollmentResponse, IPathwayEnrollmentStep, IPathwayNextStepResponse, IQuizAttemptResult, IPracticeCallAttemptResult } from 'src/app/interfaces/ipathway-enrollment';
import { PathwayEnrollmentStatus, PathwayStepState, PathwayStepType, isRoleplayStepType } from 'src/app/utils/enums/learner/pathway.enum';
import { FeatureFlagsService } from 'src/app/services/feature-flags.service';
import { AppConstants } from 'src/app/app-constants';
import { LocaleService } from 'src/app/services/locale.service';
import { PathwayStepPollingService } from './services/pathway-step-polling.service';
import { PathwayRoleplayResultsService } from '../../results/results-pathway-roleplay/service/pathway-roleplay-results.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';
import { ResultsConversationalService } from 'src/app/services/learner/results-conversational.service';

export interface PathwayStep {
  title: string;
  description: string;
  ctaLabel: string;
  ctaTooltipText: string;
  state: StepState;
  stepState: PathwayStepState;
  content: string;
  stepUuid: string;
  enrollmentStepUuid: string;
  stepType: PathwayStepType;
  needsReview: boolean;
  optional: boolean;
  highlighted?: boolean;
  maxAttemptsAllowed?: number | null;
  isLatestDebriefCompleted?: boolean;
  hasDebrief?: boolean | null;
  attemptsRemaining?: number | null;
  quizAttempts?: IQuizAttemptResult[];
  practiceCallAttempts?: IPracticeCallAttemptResult[];
  hasVisualAid?: boolean | null;
}

@Component({
  selector: 'app-pathway-details',
  standalone: true,
  templateUrl: './pathway-details.component.html',
  styleUrl: './pathway-details.component.scss',
  providers: [PathwayStepPollingService, PathwayRoleplayResultsService, QaiToastService],
  imports: [
    CommonModule,
    QaiInnerWrapComponent,
    QaiStepFlowComponent,
    QaiStepCardComponent,
    QaiStepTableComponent,
    QaiLoadingComponent,
    QaiAlertComponent,
    QaiButtonComponent,
    QaiBadgeComponent,
    QaiToastComponent,
    TranslocoModule,
  ],
})
export class PathwayDetailsComponent implements OnInit, OnDestroy {
  readonly PathwayStepType = PathwayStepType;
  readonly PathwayStepState = PathwayStepState;
  readonly isRoleplayStepType = isRoleplayStepType;

  onAllStepsComplete() {
    throw new Error('Method not implemented.');
  }
  isTranslationLoaded = false;
  pathwaySteps: PathwayStep[] = [];
  loading = false;
  enrollmentData: IPathwayEnrollmentResponse | null = null;
  nextStepData: IPathwayNextStepResponse | null = null;
  moduleAssignmentUuid: string | null = null;
  practiceType: string = 'PATHWAY'; // Default to PATHWAY if not provided
  hasStepOutOfAttempts = false;
  animateStepFlow = false;
  showAssessmentPersonalizationError = false;
  private assessmentPollingInterval: ReturnType<typeof setInterval> | null = null;
  private assessmentPollingStartTime = 0;
  private readonly assessmentPollingDurationMs = AppConstants.RESULTS_POLLING_DURATION_MS;
  @Input() pathwayName: string = '';
  enrollmentLoaded = output<IPathwayEnrollmentResponse>();
  private reviewMaterialButtonEnabled = false;
  private readonly destroy$ = new Subject<void>();

  get isEnrollmentCompleted(): boolean {
    return this.enrollmentData?.status === PathwayEnrollmentStatus.COMPLETED;
  }

  closeClick(_event: Event): void {
    this.showAssessmentPersonalizationError = false;
  }

  goToLearnerDashboard(): void {
    localStorage.setItem('returnUrl', '/assignments');
    this.router.navigate(['/assignments']);
  }

  constructor(
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private localeService: LocaleService,
    private route: ActivatedRoute,
    private router: Router,
    private pathwaysService: PathwaysService,
    private featureFlagsService: FeatureFlagsService,
    public readonly callStepService: PathwayStepPollingService,
    private readonly resultsConversationalService: ResultsConversationalService,
    private readonly toastService: QaiToastService,
    private readonly loggerService: LoggerService,
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.callStepService.stopPolling();
    this.stopAssessmentPolling();
  }

  async ngOnInit(): Promise<void> {
    const practiceCallUuid = PathwayLocalStorageUtil.getAssessmentPracticeCallUuid();
    const cameFromAssessment = PathwayLocalStorageUtil.getAssessmentExitAnimate();

    if (cameFromAssessment) {
      PathwayLocalStorageUtil.clearAssessmentExitAnimate();
    } else {
      if (PathwayLocalStorageUtil.getAssessmentPersonalizationError()) {
        PathwayLocalStorageUtil.clearAssessmentPersonalizationError();
        this.showAssessmentPersonalizationError = true;
      }
    }

    this.featureFlagsService
      .isFlagEnabled(AppConstants.PATHWAY_REVIEW_MATERIAL)
      .then((isEnabled) => {
        this.reviewMaterialButtonEnabled = isEnabled;
      })
      .catch((error) => {
        console.error(
          'Error loading pathway review material feature flag',
          error,
        );
        this.reviewMaterialButtonEnabled = false;
      });

    // 'assignment' is only used for one label, so a failed load must not keep the page hidden.
    Promise.all([
      this.translationService.loadScopedTranslation('learningPathway'),
      this.translationService.loadScopedTranslation('assignment').catch(() => undefined),
    ]).then(() => {
      this.isTranslationLoaded = true;
    });

    this.callStepService.shouldRefreshEnrollment$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const toastId = `toast-${Date.now()}`;
        const quizUuid = this.callStepService.lastReadyQuizPracticeCallUuid;
        const practiceCallUuid = this.callStepService.lastReadyPracticeCallUuid;
        const isQuiz = !!quizUuid && !practiceCallUuid;

        const step = isQuiz
          ? this.pathwaySteps.find(s => s.stepType === PathwayStepType.QUIZ && s.quizAttempts?.some(a => a.practiceCallUuid === quizUuid))
          : this.pathwaySteps.find(s => s.practiceCallAttempts?.some(a => a.practiceCallUuid === practiceCallUuid));

        this.toastService.addMessage({
          id: toastId,
          kind: 'neutral',
          iconShape: 'square',
          title: this.translocoService.translate('TOAST_TITLE', { stepName: step?.title ?? '' }, 'learningPathway'),
          content: this.translocoService.translate('TOAST_CONTENT', {}, 'learningPathway'),
          buttons: [
            {
              label: this.translocoService.translate('DISMISS', {}, 'learningPathway'),
              kind: 'tertiary-text',
              onClick: () => this.toastService.removeMessage(toastId),
            },
            {
              label: this.translocoService.translate('VIEW_RESULTS', {}, 'learningPathway'),
              onClick: () => {
                this.toastService.removeMessage(toastId);
                if (isQuiz && quizUuid) {
                  this.router.navigate(['/results_guided'], {
                    queryParams: { practiceCallUuid: quizUuid, practiceType: this.practiceType, pathwayName: this.pathwayName, stepName: step?.title ?? '' },
                  });
                } else if (practiceCallUuid) {
                  this.callStepService.markResultsViewed(practiceCallUuid);
                  this.navigateToConversationalResults(practiceCallUuid, step?.enrollmentStepUuid, this.enrollmentData?.enrollmentUuid ?? undefined, step?.title);
                }
              },
            },
          ],
        });
        if (this.moduleAssignmentUuid) {
          this.fetchEnrollment(this.moduleAssignmentUuid).then(() => {
            this.initPollingForCurrentSteps();
          });
        }
      });

    const queryParams = this.route.snapshot.queryParams;

    this.moduleAssignmentUuid = queryParams['moduleAssignmentUuid'] || null;
    this.practiceType = queryParams['practiceType'] || 'PATHWAY';

    if (cameFromAssessment && practiceCallUuid) {
      PathwayLocalStorageUtil.clearAssessmentPracticeCallUuid();
      this.startAssessmentResultsPolling(practiceCallUuid);
    } else {
      if (cameFromAssessment) {
        this.showAssessmentPersonalizationError = true;
        PathwayLocalStorageUtil.clearAssessmentPracticeCallUuid();
      }
      if (this.moduleAssignmentUuid) {
        await this.loadPathwayEnrollment(this.moduleAssignmentUuid);
        this.initPollingForCurrentSteps();
      } else {
        console.error(
          'moduleAssignmentUuid is required to load pathway enrollment',
        );
      }
    }
  }

  private stopAssessmentPolling(): void {
    if (this.assessmentPollingInterval) {
      clearInterval(this.assessmentPollingInterval);
      this.assessmentPollingInterval = null;
    }
  }

  private startAssessmentResultsPolling(practiceCallUuid: string): void {
    this.loading = true;
    this.assessmentPollingStartTime = Date.now();

    const poll = async (): Promise<void> => {
      try {
        const res = await firstValueFrom(
          this.resultsConversationalService.getResultsPromptTypeData(practiceCallUuid, AppConstants.ASSESSMENT_RESULTS_PROMPT_TYPE)
        );
        if (res?.contentDetails) {
          this.stopAssessmentPolling();
          if (this.moduleAssignmentUuid) {
            await this.loadPathwayEnrollment(this.moduleAssignmentUuid);
          } else {
            this.loading = false;
          }
          this.animateStepFlow = true;
          return;
        }
      } catch {
        // continue polling on transient errors
      }

      const elapsed = Date.now() - this.assessmentPollingStartTime;
      if (elapsed >= this.assessmentPollingDurationMs) {
        this.stopAssessmentPolling();
        this.loading = false;
        this.showAssessmentPersonalizationError = true;
        if (this.moduleAssignmentUuid) {
          await this.loadPathwayEnrollment(this.moduleAssignmentUuid);
        }
      }
    };

    poll();
    this.assessmentPollingInterval = setInterval(() => poll(), AppConstants.RESULTS_POLLING_INTERVAL_MS);
  }

  async loadPathwayEnrollment(moduleAssignmentUuid: string): Promise<void> {
    this.loading = true;
    try {
      await this.fetchEnrollment(moduleAssignmentUuid);
    } catch (error) {
      console.error('Error loading pathway enrollment:', error);
    } finally {
      this.loading = false;
    }
  }

  private async fetchEnrollment(moduleAssignmentUuid: string): Promise<void> {
    const enrollmentResponse = await firstValueFrom(
      this.pathwaysService.getorCreatePathwayEnrollment(moduleAssignmentUuid),
    );

    this.enrollmentData = enrollmentResponse;
    this.enrollmentLoaded.emit(enrollmentResponse);

    // If enrollment doesn't exist, skip getNextStep API call
    if (!enrollmentResponse.enrollmentExists || !enrollmentResponse.enrollmentUuid) {
      this.pathwaySteps = this.extractStepsFromEnrollment(enrollmentResponse, null);
      console.log('extractStepsFromEnrollment - enrollment does not exist', this.pathwaySteps);
      return;
    }

    // If enrollment is already completed, skip getNextStep API call
    if (enrollmentResponse.status === PathwayEnrollmentStatus.COMPLETED) {
      // Extract steps directly - all will be marked as completed with no CTA buttons
      this.pathwaySteps = this.extractStepsFromEnrollment(enrollmentResponse, null);
      console.log('extractStepsFromEnrollment after enrollment completed', this.pathwaySteps);
      return;
    }

    try {
      const nextStepResponse = await firstValueFrom(
        this.pathwaysService.getNextStepByEnrollmentUuid(enrollmentResponse.enrollmentUuid),
      );
      this.nextStepData = nextStepResponse;
      // Extract steps with the active step UUID from next step response
      this.pathwaySteps = this.extractStepsFromEnrollment(
        enrollmentResponse,
        nextStepResponse.toStepUuid,
        nextStepResponse.status,
        nextStepResponse.stepsToReview ?? null,
      );
      console.log('extractStepsFromEnrollment after nextStepAPI', this.pathwaySteps);
    } catch (error) {
      console.error('Error loading next step:', error);
      // If enrollment was loaded but next step failed, still show steps
      this.pathwaySteps = this.extractStepsFromEnrollment(enrollmentResponse, null);
      console.log('extractStepsFromEnrollment', this.pathwaySteps);
    }
  }

  protected quizHasAttempts(step: PathwayStep): boolean {
    return (step.quizAttempts?.length ?? 0) > 0;
  }

  isStepCtaButtonVisible(step: PathwayStep): boolean {
    return step.stepType !== PathwayStepType.QUIZ && !isRoleplayStepType(step.stepType) && !!step.ctaLabel;
  }

  isStepHeaderVisible(step: PathwayStep): boolean {
    return step.stepType !== PathwayStepType.QUIZ && !isRoleplayStepType(step.stepType);
  }

  private getDisplayableSteps(steps: IPathwayEnrollmentStep[]): IPathwayEnrollmentStep[] {
    const excludedStates = new Set([PathwayStepState.ABANDONED]);
    const excludedTypes = new Set([PathwayStepType.ASSESSMENT]);
    return steps.filter(
      (step) => !(step.stepState && excludedStates.has(step.stepState)) && !excludedTypes.has(step.type),
    );
  }

  private initPollingForCurrentSteps(): void {
    if (!this.enrollmentData || !this.moduleAssignmentUuid) return;
    this.callStepService.checkAndStartPolling(this.pathwaySteps, this.moduleAssignmentUuid);
  }

  extractStepsFromEnrollment(
    enrollment: IPathwayEnrollmentResponse,
    activeStepUuid: string | null = null,
    nextStepStatus: string | null = null,
    stepsToReview: string[] | null = null,
  ): PathwayStep[] {
    // Reset out-of-attempts banner flag before recalculating steps
    this.hasStepOutOfAttempts = false;

    // Collect all steps from all focus area groups with their stepSequence
    const allSteps: IPathwayEnrollmentStep[] = [];
    enrollment.focusAreaGroups.forEach((group) => {
      allSteps.push(...group.steps);
    });

    // Once the backend no longer lists the exited call, the mark has done its job.
    const exitedUuid = PathwayLocalStorageUtil.getRoleplayExitedUuid();
    const isExitedCallListed = allSteps.some((s) =>
      ((s.results ?? []) as IPracticeCallAttemptResult[]).some((r) => r.practiceCallUuid === exitedUuid),
    );
    if (exitedUuid && !isExitedCallListed) {
      PathwayLocalStorageUtil.clearRoleplayExited(exitedUuid);
    }

    const filteredSteps = this.getDisplayableSteps(allSteps);

    // Sort by stepSequence to maintain proper order
    filteredSteps.sort((a, b) => a.stepSequence - b.stepSequence);

    // If getNextStep returned BLOCKED status with no toStepUuid,
    // find the first OUT_OF_ATTEMPTS step so we can disable all steps after it
    const outOfAttemptsIndex = (enrollment.status === PathwayEnrollmentStatus.LOCKED)
      ? filteredSteps.findIndex(s => s.stepState === PathwayStepState.OUT_OF_ATTEMPTS)
      : -1;

    // If activeStepUuid is provided from getNextStep API, mark that step as IN_PROGRESS
    // This ensures the correct step shows the "Continue" button
    if (activeStepUuid) {
      const activeStep = filteredSteps.find(
        (step) => step.stepUuid === activeStepUuid,
      );
      if (activeStep) {
        activeStep.stepState = PathwayStepState.IN_PROGRESS;
      }
    }

    const furthestEligibleStep = filteredSteps.find(
      s => s.enrollmentStepUuid === enrollment.furthestEligibleEnrollmentStepUuid
    );
    const furthestEligibleSequence = furthestEligibleStep?.stepSequence;

    const focusAreasNeedingReview = this.getReviewHighlightBounds(filteredSteps);

    // First pass: Check if there's any IN_PROGRESS step
    // This determines whether we show "Continue" or "Begin" button
    // At any moment, only one active button can appear: either "Continue" (IN_PROGRESS) or "Begin" (first NOT_STARTED)
    const hasInProgressStep = filteredSteps.some(
      (step) => step.stepState === PathwayStepState.IN_PROGRESS,
    );

    const lastMustConsumeIndex = filteredSteps.reduce(
      (last, step, i) => step.stepState === PathwayStepState.MUST_CONSUME ? i : last,
      -1,
    );

    // Track state for proper mapping
    let hasFirstNotStartedStep = false;
    let hasActiveInProgressStep = false;

    // Map to PathwayStep format
    return filteredSteps.map((step: IPathwayEnrollmentStep, index: number) => {
      if (outOfAttemptsIndex !== -1 && index > outOfAttemptsIndex) {
        return {
          title: step.title,
          description: step.description,
          ctaLabel: '',
          ctaTooltipText: '',
          state: 'disabled' as StepState,
          stepState: step.stepState ?? PathwayStepState.NOT_STARTED,
          content: step.description,
          stepUuid: step.stepUuid,
          enrollmentStepUuid: step.enrollmentStepUuid ?? '',
          stepType: step.type,
          needsReview: !!step.needsReview,
          optional: step.optional ?? false,
        };
      }

      const stepState = step.stepState ?? PathwayStepState.NOT_STARTED;

      if (stepState === PathwayStepState.OUT_OF_ATTEMPTS) {
        this.hasStepOutOfAttempts = true;
      }

      // Check if this is the first IN_PROGRESS step (only one can be active)
      const isFirstInProgress =
        stepState === PathwayStepState.IN_PROGRESS &&
        !hasActiveInProgressStep;

      // Check if this is the first NOT_STARTED step (only if no IN_PROGRESS step exists)
      const isFirstNotStarted =
        stepState === PathwayStepState.NOT_STARTED &&
        !hasFirstNotStartedStep &&
        !hasInProgressStep;

      // Map step state to component state based on PathwayStepState enum
      const state = this.mapStepStateToComponentState(
        stepState,
        step.isFurthest,
        hasInProgressStep,
        hasActiveInProgressStep,
        hasFirstNotStartedStep,
      );

      // Update tracking flags after mapping
      if (isFirstInProgress) {
        hasActiveInProgressStep = true;
      }
      if (isFirstNotStarted) {
        hasFirstNotStartedStep = true;
      }

      // Determine CTA label based on step state
      let ctaLabel = '';
      ctaLabel = this.getCtaLabelForStep(stepState, step.type);
      const needsReview = !!step.needsReview;
      const quizBounds = focusAreasNeedingReview.get(step.focusAreaUuid);
      const highlighted =
        step.type === PathwayStepType.LEARN_WITH_X &&
        needsReview &&
        quizBounds !== undefined &&
        step.stepSequence < quizBounds.quizSeq &&
        step.stepSequence > quizBounds.lowerBound;
        
      const ctaTooltipText = highlighted
        ? this.translocoService.translate(
            'REVIEW_BEFORE_QUIZ_TOOLTIP',
            {},
            'learningPathway',
          )
        : '';

      const pathwayStep: PathwayStep = {
        title: step.title,
        description: step.description,
        ctaLabel,
        ctaTooltipText,
        state: highlighted ? ('warning' as StepState) : state,
        stepState,
        content: step.description,
        stepUuid: step.stepUuid,
        enrollmentStepUuid: step.enrollmentStepUuid ?? '',
        stepType: step.type,
        needsReview,
        optional: step.optional ?? false,
        highlighted,
        isLatestDebriefCompleted: step.isLatestDebriefCompleted,
        hasDebrief: step.hasDebrief ?? null,
        maxAttemptsAllowed: step.maxAttemptsAllowed ?? null,
        hasVisualAid: step.hasVisualAid ?? null,
      };
      if (step.type === PathwayStepType.QUIZ) {
        pathwayStep.quizAttempts = (step.results ?? []) as IQuizAttemptResult[];
        if (step.maxAttemptsAllowed != null && step.totalAttempts != null) {
          const attemptsRemaining = this.getAttemptsRemaining(
            step.maxAttemptsAllowed,
            step.totalAttempts
          );
          pathwayStep.attemptsRemaining = attemptsRemaining;
          if (
            attemptsRemaining <= 0 &&
            step.stepState !== PathwayStepState.COMPLETED
          ) {
            this.hasStepOutOfAttempts = true;
          }
        }
      }
      if (step.type === PathwayStepType.PRACTICE_CALL || step.type === PathwayStepType.CERTIFICATION_CALL) {
          // The page can load before the exit request for a call left mid-way has finished.
          const exitedUuid = PathwayLocalStorageUtil.getRoleplayExitedUuid();
          const attempts = (step.results ?? []) as IPracticeCallAttemptResult[];
          pathwayStep.practiceCallAttempts = attempts.filter((a) => a.practiceCallUuid !== exitedUuid);
          if (pathwayStep.practiceCallAttempts.length < attempts.length) {
            this.loggerService.log('[PATHWAY] hiding attempt that was exited while the page loaded', { practiceCallUuid: exitedUuid });
          }
          pathwayStep.attemptsRemaining = step.maxAttemptsAllowed == null
            ? null
            : step.totalAttempts != null
              ? this.getAttemptsRemaining(step.maxAttemptsAllowed, step.totalAttempts)
              : null;
          if (
            pathwayStep.attemptsRemaining != null && pathwayStep.attemptsRemaining <= 0 &&
            step.stepState !== PathwayStepState.COMPLETED
          ) {
            this.hasStepOutOfAttempts = true;
          }
        }

      // Optional steps at or before the furthest eligible step show as completed; steps beyond it (or when no furthest exists) are disabled.
      if (step.optional) {
        if (furthestEligibleSequence !== undefined && step.stepSequence <= furthestEligibleSequence) {
          pathwayStep.state = 'completed' as StepState;
        } else {
          pathwayStep.state = 'disabled' as StepState;
        }
      }

      if (lastMustConsumeIndex !== -1 && index > lastMustConsumeIndex) {
        pathwayStep.state = 'disabled' as StepState;
      }

      return pathwayStep;
    });
  }

  private getReviewHighlightBounds(
    steps: IPathwayEnrollmentStep[],
  ): Map<string | null, { quizSeq: number; lowerBound: number }> {
    const bounds = new Map<string | null, { quizSeq: number; lowerBound: number }>();
    const lastQuizSeq = new Map<string | null, number>();

    for (const step of steps) {
      if (step.type !== PathwayStepType.QUIZ) continue;

      if (step.needsReview && step.stepState !== PathwayStepState.COMPLETED && !bounds.has(step.focusAreaUuid)) {
        bounds.set(step.focusAreaUuid, {
          quizSeq: step.stepSequence,
          lowerBound: lastQuizSeq.get(step.focusAreaUuid) ?? 0,
        });
      }

      lastQuizSeq.set(step.focusAreaUuid, step.stepSequence);
    }

    return bounds;
  }

  getAttemptsRemaining(
    maxAttemptsAllowed: number,
    totalAttempts: number
  ): number {
    const used = Math.min(totalAttempts, maxAttemptsAllowed);
    return Math.max(0, maxAttemptsAllowed - used);
  }

  /**
   * Maps PathwayStepState enum values to StepState from qai/ui
   * @param stepState - The step state from the API (nullable)
   * @param isFurthest - Whether this is the furthest step reached
   * @param hasInProgressStep - Whether there's any IN_PROGRESS step in the entire list
   * @param hasActiveInProgressStep - Whether we've already activated an IN_PROGRESS step (only one can be active)
   * @param hasFirstNotStartedStep - Whether we've already seen the first NOT_STARTED step
   * @returns StepState type value from qai/ui
   */
  private mapStepStateToComponentState(
    stepState: PathwayStepState | null,
    isFurthest: boolean,
    hasInProgressStep: boolean,
    hasActiveInProgressStep: boolean,
    hasFirstNotStartedStep: boolean,
  ): StepState {
    const state = stepState ?? PathwayStepState.NOT_STARTED;

    switch (state) {
      case PathwayStepState.COMPLETED:
        // Multiple completed steps are allowed
        return 'completed' as StepState;

      case PathwayStepState.IN_PROGRESS:
        // Only one IN_PROGRESS step can be active at any moment (shows "Continue" button)
        // If we've already activated one, this one should be disabled
        // This ensures only one "Continue" button appears
        return hasActiveInProgressStep
          ? ('disabled' as StepState)
          : ('active' as StepState);

      case PathwayStepState.OUT_OF_ATTEMPTS:
        return 'active' as StepState;

      case PathwayStepState.NOT_STARTED:
        // If there's an IN_PROGRESS step (Continue button exists), all NOT_STARTED steps should be disabled
        // This ensures "Continue" and "Begin" buttons never appear at the same time
        if (hasInProgressStep) {
          return 'disabled' as StepState;
        }
        // If no IN_PROGRESS step, the very first NOT_STARTED step should be active (shows "Begin" button)
        // Subsequent NOT_STARTED steps should be disabled
        if (!hasFirstNotStartedStep) {
          return 'active' as StepState;
        }
        // Subsequent NOT_STARTED steps are disabled
        return 'disabled' as StepState;

      case PathwayStepState.STOPPED:
        // Stopped steps are treated as disabled (multiple disabled allowed)
        return 'disabled' as StepState;

      case PathwayStepState.MUST_CONSUME:
        return 'active' as StepState;

      default:
        return 'disabled' as StepState;
    }
  }

  /**
   * Gets the appropriate CTA label based on step state
   * @param stepState - The step state from the API (nullable)
   * @returns CTA label string
   */
  private getCtaLabelForStep(
    stepState: PathwayStepState | null,
    stepType: PathwayStepType,
  ): string {
    const state = stepState ?? PathwayStepState.NOT_STARTED;

    if (stepType === PathwayStepType.QUIZ || isRoleplayStepType(stepType)) {
      return '';
    }

    switch (state) {
      case PathwayStepState.IN_PROGRESS:
        return this.translocoService.translate(
          'CONTINUE',
          {},
          'learningPathway',
        );

      case PathwayStepState.OUT_OF_ATTEMPTS:
        return '';

      case PathwayStepState.COMPLETED:
        if (stepType === PathwayStepType.INTRO) {
          return this.translocoService.translate(
            'ASK_QUESTIONS_ABOUT_PATHWAY',
            {},
            'learningPathway',
          );
        }
        if (!this.reviewMaterialButtonEnabled) {
          return '';
        }
        return this.translocoService.translate(
          'REVIEW_MATERIAL',
          {},
          'learningPathway',
        );

      case PathwayStepState.NOT_STARTED:
        return this.translocoService.translate('BEGIN', {}, 'learningPathway');

      case PathwayStepState.STOPPED:
        // Stopped steps are treated as disabled, but if shown, use Continue
        return this.translocoService.translate(
          'CONTINUE',
          {},
          'learningPathway',
        );

      case PathwayStepState.ABANDONED:
        // This case should not be reached as abandoned steps are filtered out
        return this.translocoService.translate('BEGIN', {}, 'learningPathway');

      case PathwayStepState.MUST_CONSUME:
        return this.translocoService.translate('CONTINUE', {}, 'learningPathway');

      default:
        return this.translocoService.translate('BEGIN', {}, 'learningPathway');
    }
  }

  /**
   * Get the current enrollment status
   * @returns The enrollment status enum value, or null if no enrollment data
   */
  getEnrollmentStatus(): PathwayEnrollmentStatus | null {
    return this.enrollmentData?.status || null;
  }

  /**
   * Check if enrollment is in a specific status
   * @param status - The status to check
   * @returns True if enrollment is in the specified status
   */
  isEnrollmentStatus(status: PathwayEnrollmentStatus): boolean {
    return this.enrollmentData?.status === status;
  }

  /**
   * Handles step button click (Begin/Continue/Review material)
   * Navigates to the call route with required parameters
   * @param step - The pathway step that was clicked
   */
  onStepClick(step: PathwayStep): void {
    if (!this.enrollmentData || !this.moduleAssignmentUuid) {
      console.error('Enrollment data or moduleAssignmentUuid is missing');
      return;
    }

    // Check if enrollment doesn't exist - need to create it first
    if (!this.enrollmentData.enrollmentExists && !this.enrollmentData.enrollmentUuid) {
      console.log('Enrollment does not exist, creating enrollment before proceeding');
      this.loading = true;

      this.pathwaysService.createEnrollment(this.moduleAssignmentUuid).subscribe({
        next: (enrollmentResponse: IPathwayEnrollmentResponse) => {
          console.log('Enrollment created successfully:', enrollmentResponse);
          this.enrollmentData = enrollmentResponse;

          // Now navigate to call route with the new enrollmentUuid
          const enrollmentId = enrollmentResponse.enrollmentUuid;
          const stepUuid = step.enrollmentStepUuid || enrollmentResponse.activeEnrollmentStepUuid;
          const stepType = step.stepType;

          this.loading = false;

          if (stepType === PathwayStepType.ASSESSMENT) {
            this.navigateToPreAssessmentCallScreen(enrollmentId, stepUuid);
          } else {
            this.router.navigate(['/call'], {
              queryParams: {
                enrollmentUuid: enrollmentId,
                moduleAssignmentUuid: this.moduleAssignmentUuid,
                enrollmentStepUuid: stepUuid,
                stepType: stepType,
                practiceType: this.practiceType,
                maxAttemptsAllowed: step.maxAttemptsAllowed ?? null,
              },
            });
          }
        },
        error: (error) => {
          console.error('Error creating enrollment:', error);
          this.loading = false;
        },
      });

      return;
    }

    const enrollmentId = this.enrollmentData.enrollmentUuid;
    const stepUuid = step.enrollmentStepUuid;
    const stepType = step.stepType;

    if (stepType === PathwayStepType.ASSESSMENT) {
      this.navigateToPreAssessmentCallScreen(enrollmentId, stepUuid);
      return;
    }

    // Navigate to call route with query parameters
    // Note: For PATHWAY type, stepUuid is used instead of callUuid
    this.router.navigate(['/call'], {
      queryParams: {
        enrollmentUuid: enrollmentId,
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        enrollmentStepUuid: stepUuid,
        stepType: stepType,
        practiceType: this.practiceType,
        maxAttemptsAllowed: step.maxAttemptsAllowed ?? null,
        stepName: step.title ?? '',
      },
    });
  }

  onRolePlayStepActionClick(step: PathwayStep): void {
    if (this.callStepService.shouldRequireViewResults(step)) {
      const uuid = this.callStepService.getMostRecentAttempt(step.practiceCallAttempts ?? [])?.practiceCallUuid;
      if (!uuid) return;
      this.callStepService.markResultsViewed(uuid);
      this.navigateToRoleplayResults(step, uuid);
      return;
    }
    this.onStepClick(step);
  }

  private navigateToPreAssessmentCallScreen(enrollmentId: string | null | undefined, stepUuid: string | null | undefined): void {
    this.router.navigate(['/assessment-pre-call'], {
      queryParams: {
        enrollmentUuid: enrollmentId,
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        enrollmentStepUuid: stepUuid,
        practiceType: this.practiceType,
      },
    });
  }

  protected navigateToRoleplayResults(step: PathwayStep, practiceCallUuid: string): void {
    this.router.navigate(['/call'], {
      queryParams: {
        practiceType: this.practiceType,
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        enrollmentStepUuid: step.enrollmentStepUuid,
        enrollmentUuid: this.enrollmentData?.enrollmentUuid,
        stepType: step.stepType,
        practiceCallUuid,
        viewResults: 'true',
        stepTitle: step.title,
        ...(step.stepState === PathwayStepState.OUT_OF_ATTEMPTS && {
          outOFAttempts: 'true',
        }),
      },
    });
  }

  getBreadcrumbItems(): BreadcrumbItem[] {
    return [
      {
        text: this.translocoService.translate(
          'ASSIGNMENTS',
          {},
          'learningPathway',
        ),
        href: '/assignments',
      },
      { text: '', href: '', active: true },
    ];
  }

  getOverallScoreTooltip(): string {
    return this.translocoService.translate('QUIZ_OVERALL_SCORE_TOOLTIP', {}, 'learningPathway');
  }

  getQuizColumnHeaders(): HeaderConfig[] {
    const scope = 'learningPathway';
    return [
      { name: this.translocoService.translate('COLUMN_DATE', {}, scope), fieldKey: 'date', sortable: false },
      { name: this.translocoService.translate('COLUMN_OVERALL_SCORE', {}, scope), fieldKey: 'overallScore', sortable: false },
      { name: this.translocoService.translate('COLUMN_ANSWER_POINTS', {}, scope), fieldKey: 'answerPoints', sortable: false },
      { name: this.translocoService.translate('COLUMN_RESULTS', {}, scope), fieldKey: 'results', sortable: false, clickable: true },
    ];
  }

  getSortedQuizAttempts(step: PathwayStep): QuizAttempt[] {
    if (!step.quizAttempts?.length) return [];

    const isStepCompleted = step.stepState === PathwayStepState.COMPLETED;
    const sorted = step.quizAttempts.slice().sort((a, b) => {
      if (isStepCompleted) {
        if (b.overAllScore !== a.overAllScore) {
          return b.overAllScore - a.overAllScore;
        }
      }
      return new Date(b.scoredAt).getTime() - new Date(a.scoredAt).getTime();
    });

    return this.mapQuizAttempts(sorted);
  }

  mapQuizAttempts(results: IQuizAttemptResult[] | undefined): QuizAttempt[] {
    if (!results?.length) return [];
    return results.map((r) => ({
      id: r.practiceCallUuid,
      date: this.localeService.formatTableDateTime(r.scoredAt),
      overallScore: this.localeService.formatPercentage(
        r.overAllScore,
        this.localeService.getDecimalPlaces(r.overAllScore, 2)
      ),
      answerPoints: `${r.correctAnswerPoints}/${r.totalAnswerPoints}`,
    }));
  }

  private navigateToConversationalResults(practiceCallUuid: string, enrollmentStepUuid?: string, enrollmentUuid?: string, stepTitle?: string): void {
    const queryParams: { [key: string]: string } = {
      practiceCallUuid,
    };
    if (this.moduleAssignmentUuid) {
      queryParams['moduleAssignmentUuid'] = this.moduleAssignmentUuid;
    }
    if (enrollmentStepUuid) {
      queryParams['enrollmentStepUuid'] = enrollmentStepUuid;
    }
    if (enrollmentUuid) {
      queryParams['enrollmentUuid'] = enrollmentUuid;
    }
    if (stepTitle) {
      queryParams['stepTitle'] = stepTitle;
    }
    if (this.pathwayName) {
      queryParams['pathwayName'] = this.pathwayName;
    }

    const resultsPath = '/results_conversational';
    const returnUrl = `${resultsPath}?${new URLSearchParams(queryParams).toString()}`;
    localStorage.setItem('returnUrl', encodeURIComponent(returnUrl));

    this.router.navigate([resultsPath], { queryParams });
  }

  onViewFeedbackClick(payload: { attempt: QuizAttempt | RolePlayAttempt; event?: Event }, isPracticeCall = false): void {
    const practiceCallUuid = payload?.attempt?.id;
    if (!practiceCallUuid) return;
    if (isPracticeCall) {
      const uuid = String(practiceCallUuid);
      if (this.callStepService.shouldSuppressRowViewFeedback(uuid, this.pathwaySteps)) {
        payload?.event?.preventDefault?.();
        return;
      }
      this.callStepService.markResultsViewed(uuid);
      const step = this.pathwaySteps.find(s =>
        s.practiceCallAttempts?.some(a => a.practiceCallUuid === uuid)
      );
      this.navigateToConversationalResults(uuid, step?.enrollmentStepUuid, this.enrollmentData?.enrollmentUuid ?? undefined, step?.title);
      return;
    }

    const step = this.pathwaySteps.find(s =>
      s.quizAttempts?.some(a => a.practiceCallUuid === String(practiceCallUuid))
    );
    const resultsPath = step?.hasVisualAid ? '/results_visual_aid' : '/results_guided';

    this.router.navigate([resultsPath], {
      queryParams: {
        practiceCallUuid,
        practiceType: this.practiceType,
        pathwayName: this.pathwayName,
      },
    });
  }

  onViewAllAttemptsClick(step: PathwayStep): void {
    console.log("viewallattempts button click")
  }

  onRolePlayStepFooterCtaClick(step: PathwayStep): void {
    const uuid = this.callStepService.getMostRecentAttempt(step.practiceCallAttempts ?? [])?.practiceCallUuid;
    if (!uuid) return;
    this.callStepService.markResultsViewed(uuid);
    this.router.navigate(['/call'], {
      queryParams: {
        practiceType: this.practiceType,
        moduleAssignmentUuid: this.moduleAssignmentUuid,
        enrollmentStepUuid: step.enrollmentStepUuid,
        enrollmentUuid: this.enrollmentData?.enrollmentUuid,
        stepType: step.stepType,
        practiceCallUuid: uuid,
        startDebrief: 'true',
        stepTitle: step.title,
      },
    });
  }
}
